import React from 'react';
import { Box, Notification, NotificationType } from 'edenred-ui';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';

import { getFirstDayOfNextMonth } from '@epi-helpers/dateHelper';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import { FundsLoadingTypes } from '@epi-constants/benefitTypes';
import { ICreateBenefitGroupForm } from '@epi-models/pages/BenefitGroups';
import { useGetDefaultBenefitGroupsSettingsQuery } from '@epi-api/benefitGroups';

type GeneralSettingsDirectLoadNotificationProps = {
  isHidden: boolean;
};

enum NotificationKeys {
  Info = 'notificationInfo',
  PendingSchedule = 'notificationInfoPendingSchedule'
}

const GeneralSettingsLoadTypeNotification = ({
  isHidden
}: GeneralSettingsDirectLoadNotificationProps) => {
  const {
    isPendingScheduledLoadsForBenefit,
    isFundsLoadingTypePending,
    lunchGlobalSettings
  } = useGetDefaultBenefitGroupsSettingsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      isPendingScheduledLoadsForBenefit:
        data?.lunchGlobalSettings?.isPendingScheduledLoadsForBenefit,
      isFundsLoadingTypePending:
        data?.lunchGlobalSettings?.isFundsLoadingTypePending,
      lunchGlobalSettings: data?.lunchGlobalSettings
    }),
    refetchOnMountOrArgChange: false
  });
  const { t } = useTranslation();
  const language = useSelector(getApplicationLanguageId);
  const { watch, formState } = useFormContext<ICreateBenefitGroupForm>();

  const defaultValue =
    formState.defaultValues?.benefitSettings?.lunch?.fundsLoadingType;
  const currentValue = watch('benefitSettings.lunch.fundsLoadingType');

  const getNotificationBannerText = (notification: string) =>
    t(`components.manage_benefits.${notification}`, {
      date: getFirstDayOfNextMonth(language)
    });

  const isShowScheduledNotificationBanner =
    (currentValue === FundsLoadingTypes.TopUpRequest &&
      defaultValue !== currentValue &&
      isPendingScheduledLoadsForBenefit &&
      !isFundsLoadingTypePending) ||
    (defaultValue === currentValue &&
      isFundsLoadingTypePending &&
      isPendingScheduledLoadsForBenefit);

  const isShowBasicNotificationBanner =
    (defaultValue === currentValue &&
      isFundsLoadingTypePending &&
      !isPendingScheduledLoadsForBenefit) ||
    (defaultValue !== currentValue && !isFundsLoadingTypePending);

  if (
    (!isShowBasicNotificationBanner && !isShowScheduledNotificationBanner) ||
    isHidden ||
    !lunchGlobalSettings
  ) {
    return null;
  }

  return (
    <Box whiteSpace="pre-line">
      <Notification
        type={NotificationType.Important}
        text={getNotificationBannerText(
          isShowScheduledNotificationBanner
            ? NotificationKeys.PendingSchedule
            : NotificationKeys.Info
        )}
      />
    </Box>
  );
};

export { GeneralSettingsLoadTypeNotification };
